/**
 * Whether a newer Paige is deployed than the one this tab runs, outside React so the router, the
 * chunk-failure handler and the banner share it (`useSyncExternalStore` in UpdateBanner).
 */
export interface UpdateState {
  /** A newer Paige is deployed. Never goes back to false in a tab's life. */
  updated: boolean;
  /** The person closed the banner; it stays closed until a still newer Paige is deployed. */
  dismissed: boolean;
}

let state: UpdateState = { updated: false, dismissed: false };
let seen: string | null = null;
const listeners = new Set<() => void>();

function set(next: UpdateState): void {
  state = next;
  for (const listener of listeners) listener();
}

export function updateState(): UpdateState {
  return state;
}

export function subscribeUpdate(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * A newer Paige is deployed: `version` when known (a check), or none (a chunk that no longer
 * exists says so without saying which). A version not seen before brings a dismissed banner back.
 */
export function markUpdated(version?: string): void {
  const fresh = version !== undefined && version !== seen;
  if (version !== undefined) seen = version;
  if (state.updated && !fresh) return;
  set({ updated: true, dismissed: false });
}

export function dismissUpdate(): void {
  if (state.updated) set({ ...state, dismissed: true });
}

/** Tests only. */
export function resetUpdateStateForTests(): void {
  seen = null;
  set({ updated: false, dismissed: false });
}
